/**
 * QuestionnaireNew — /w/:slug/app/questionnaires/new.
 *
 * P8 / M1 + M2 ingest surface. Wired to lib/api.ts (PR-P2-2).
 *
 * Paste flow (M1 + M2): POST paste JSON → server runs recognizer detect +
 * inline extract → SPA navigates to the list.
 *
 * Upload flow (M1 + M2): POST XLSX → server persists to R2 → SPA fetches an
 * LLM-proposed column mapping → user confirms in a modal → SPA posts the
 * confirmed mapping → server extracts + advances status → SPA navigates to
 * the list. Match/draft/export land in M3–M6.
 *
 * PR-P2-2 — strand recovery (UC-5 R1 decision): the questionnaire row
 * persists at upload time, before the mapping is confirmed. Leaving
 * without confirming is NOT destructive — the row shows up on the list
 * as a "Needs mapping" strand with a Resume mapping → action
 * (Questionnaires.tsx) that reopens this page with `?resume=<id>`.
 * Resuming re-fetches a FRESH mapping proposal (the LLM call re-runs;
 * nothing about the user's earlier column choices is persisted — see
 * the ux-adversary flow-critic finding this comment used to overclaim
 * "the same" proposal). Cancelling (either fresh or resumed) just
 * returns to the list with a notice — never suggests delete-and-reupload,
 * the R1 "current-code foot-gun" this pattern replaces.
 *
 * ux-adversary critique pass (2026-08-27, Marcus + Nadia persona critics
 * + aesthetic + flow critics) fixed in this PR: the resume branch always
 * renders an exit (Marcus/aesthetic/flow Blocker/High — 3 independent
 * critics caught the same dead-end: an error or an already-resolved
 * strand left the user with nothing to click); a 409 mapping-confirm
 * race now shows a plain-language message instead of the raw server
 * string, with the modal actions disabled (flow High); an upload that
 * succeeds but whose mapping-proposal fetch then fails no longer leaves
 * the same Upload button re-clickable — re-clicking would silently
 * create a second orphan row for the same file (flow High); a due_date
 * field now exists so the Deadline chip on the list isn't a permanent
 * dead prop (Nadia Blocker — paired with the backend delta in
 * questionnaire-service.ts / questionnaires.ts). See
 * docs/implementation-plan.md §3.2 deviation notes for what was fixed
 * vs deferred.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import { Extracting } from './Extracting';
import {
  ApiError,
  confirmMapping as confirmMappingApi,
  createQuestionnairePaste,
  getMappingProposal,
  getQuestionnaire,
  uploadQuestionnaire,
  type XlsxMappingProposal,
  type XlsxSample,
} from '../lib/api';

type Mode = 'paste' | 'upload';

const MAX_PASTE_BYTES = 512 * 1024;
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

interface MappingModalState {
  questionnaireId: string;
  title: string;
  proposal: XlsxMappingProposal;
  sample: XlsxSample;
  // Editable local copy that mirrors the proposal at open time.
  mapping: XlsxMappingProposal;
  /** Set once mapping-confirm 409s (someone else finished first) — the
   *  modal stays visible but read-only with a plain-language notice,
   *  rather than a dead-end raw error on a still-editable form. */
  conflict?: string;
  /** PR-P2-3 fix (ux-adversary persona:Marcus, Blocker): any OTHER
   *  confirm-mapping failure (transient 500, network blip) used to
   *  call the page-level `setError()` — invisible, because this modal
   *  renders as a `position: fixed` full-screen overlay on top of the
   *  page body where that Alert lives. Shown inside the modal instead,
   *  and — unlike `conflict` — doesn't lock the form, since a transient
   *  failure is worth just retrying Confirm again. */
  error?: string;
}

export function QuestionnaireNew() {
  const { workspace } = useWorkspace();
  const slug = workspace.slug;
  const navigate = useNavigate();
  const listHref = `/w/${slug}/app/questionnaires`;
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get('resume');

  const [mode, setMode] = useState<Mode>('paste');
  const [title, setTitle] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<MappingModalState | null>(null);
  const [resuming, setResuming] = useState(Boolean(resumeId));
  // Set when an upload succeeds but the follow-up mapping-proposal fetch
  // fails — keeps the row's id so the UI can offer Resume instead of
  // leaving the original (now-stale) Upload button re-clickable, which
  // would silently create a second orphan row for the same file.
  const [uploadedPendingMapping, setUploadedPendingMapping] = useState<{ id: string; title: string } | null>(null);
  // Non-error, dismissible info shown once (e.g. "already mapped by
  // someone else") — distinct from `error` so styling/wording stays
  // honest instead of red-alerting a non-failure outcome.
  const [notice, setNotice] = useState<string | null>(null);
  // PR-P2-3: set while the extract/match/draft request is in flight
  // (paste submit, or upload's confirm-mapping) — swaps the whole view
  // to <Extracting> so the wait isn't a bare "Working…" button label.
  const [extractingTitle, setExtractingTitle] = useState<string | null>(null);

  const openMappingModal = useCallback(async (questionnaireId: string, titleForDisplay: string) => {
    setError(null);
    setBusy(true);
    try {
      const data = await getMappingProposal(slug, questionnaireId);
      setModal({
        questionnaireId,
        title: titleForDisplay,
        proposal: data.proposal,
        sample: data.sample,
        mapping: { ...data.proposal },
      });
      setUploadedPendingMapping(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [slug]);

  // Resume mode: `?resume=<id>` re-opens the mapping modal for an
  // existing strand row instead of showing the new-questionnaire form.
  useEffect(() => {
    if (!resumeId) return;
    let cancelled = false;
    (async () => {
      try {
        const { questionnaire } = await getQuestionnaire(slug, resumeId);
        if (cancelled) return;
        if (questionnaire.kind !== 'upload' || questionnaire.status !== 'extracting') {
          // Already progressed by someone else (or not a resumable row
          // at all) — nothing to resume. Say so; don't silently bounce.
          setNotice('This upload was already mapped — nothing left to resume.');
          setResuming(false);
          return;
        }
        await openMappingModal(questionnaire.questionnaire_id, questionnaire.title);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setResuming(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeId, slug]);

  const submit = useCallback(async () => {
    setError(null);
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'paste') {
        if (!pasteText.trim()) throw new Error('Paste text is required.');
        if (new TextEncoder().encode(pasteText).byteLength > MAX_PASTE_BYTES) {
          throw new Error(`Paste exceeds ${Math.round(MAX_PASTE_BYTES / 1024)} KB.`);
        }
        // PR-P2-3: paste-create runs recognizer-detect + extract + match
        // + draft synchronously in one request — show progress instead
        // of a bare "Working…" button for however long that takes.
        setExtractingTitle(title.trim());
        await createQuestionnairePaste(slug, {
          title: title.trim(),
          source_text: pasteText,
          due_date: dueDate || undefined,
        });
        navigate(`${listHref}?notice=created`);
        return;
      }

      if (!file) throw new Error('Select an XLSX file to upload.');
      if (file.size > MAX_UPLOAD_BYTES) {
        throw new Error(`File exceeds ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)} MB.`);
      }
      const created = await uploadQuestionnaire(slug, { title: title.trim(), file, due_date: dueDate || undefined });
      // The row is persisted now — from here on, closing the tab or
      // hitting an error is non-destructive; the row reappears on the
      // list as a "Needs mapping" strand with a Resume mapping → action.
      setUploadedPendingMapping({ id: created.questionnaire_id, title: created.title });
      await openMappingModal(created.questionnaire_id, created.title);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      setExtractingTitle(null);
    }
  }, [mode, title, pasteText, file, dueDate, slug, listHref, navigate, openMappingModal]);

  const confirmMapping = useCallback(async () => {
    if (!modal) return;
    if (!modal.mapping.question_col) {
      // PR-P2-3 fix (ux-adversary persona:Marcus, Blocker): this used to
      // call the page-level setError(), which is invisible — the modal
      // is a position:fixed full-screen overlay on top of wherever that
      // Alert renders. Shown inside the modal instead.
      setModal((m) => (m ? { ...m, error: 'Pick a column for the question text.' } : m));
      return;
    }
    setBusy(true);
    setModal((m) => (m ? { ...m, error: undefined } : m));
    // PR-P2-3: mapping-confirm now runs extract + match + draft
    // synchronously (PR-P2-2 reordered it to extract before persisting
    // the mapping — same request, same wait). Swap to the progress view
    // instead of leaving the modal's "Extracting…" button label as the
    // only feedback.
    setExtractingTitle(modal.title);
    try {
      await confirmMappingApi(slug, modal.questionnaireId, {
        question_col: modal.mapping.question_col,
        response_col: modal.mapping.response_col,
        section_col: modal.mapping.section_col,
        evidence_col: modal.mapping.evidence_col,
      });
      setModal(null);
      navigate(`${listHref}?notice=extracted`);
    } catch (err) {
      // A 409 means someone else confirmed this same upload first (two
      // teammates, one strand). Show a plain-language message and lock
      // the modal instead of leaving a raw server string on a form the
      // user could just re-submit into the same conflict.
      if (err instanceof ApiError && err.status === 409) {
        setModal((m) => (m ? { ...m, conflict: "Someone else already finished mapping this upload." } : m));
        return;
      }
      // PR-P2-3 fix (ux-adversary persona:Marcus, Blocker): same
      // invisible-error bug — a transient failure (500, network blip)
      // used to silently drop the user back to an unchanged-looking
      // modal with no explanation. Shown inside the modal, form stays
      // editable so retrying Confirm is a single click.
      const message = err instanceof Error ? err.message : String(err);
      setModal((m) => (m ? { ...m, error: message } : m));
    } finally {
      setBusy(false);
      setExtractingTitle(null);
    }
  }, [modal, slug, listHref, navigate]);

  const cancelMapping = useCallback(() => {
    // Non-destructive either way: the row already persists (fresh upload
    // or a resumed strand) and stays on the list as "Needs mapping" until
    // someone confirms the column mapping. Never delete-and-reupload.
    setModal(null);
    navigate(`${listHref}?notice=strand-saved`);
  }, [listHref, navigate]);

  if (extractingTitle) {
    return (
      <div style={{ display: 'grid', gap: 'var(--space-6)', maxWidth: '48rem' }}>
        <PageHeader title="Extracting questions" subtitle="Sit tight — this runs automatically." />
        <Extracting title={extractingTitle} />
        {/* The card says it's safe to leave — back that claim with an
            actual exit, matching the resumeId branch below (ux-adversary
            Marcus + aesthetic, Medium — a claim with nothing to click
            makes the user hunt through the nav instead). */}
        <div>
          <Link to={listHref} style={{ textDecoration: 'none' }}>
            <Button variant="secondary">Back to questionnaires</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (resumeId) {
    return (
      <div style={{ display: 'grid', gap: 'var(--space-6)', maxWidth: '48rem' }}>
        <PageHeader title="Resume mapping" subtitle="Pick up where the upload left off." />
        {error ? <Alert variant="error">{error}</Alert> : null}
        {notice ? <Alert variant="info">{notice}</Alert> : null}
        {resuming ? <Spinner label="Loading upload" /> : null}
        {modal ? (
          <MappingModal
            state={modal}
            busy={busy}
            onChange={(next) => setModal({ ...modal, mapping: next })}
            onConfirm={confirmMapping}
            onCancel={cancelMapping}
          />
        ) : null}
        {/* Always-available exit — never leave the user with nothing to
            click on an error or an already-resolved strand (ux-adversary
            Blocker: Marcus, aesthetic, and flow critics independently
            caught the old version of this branch as a dead end). */}
        {!resuming && !modal ? (
          <div>
            <Link to={listHref} style={{ textDecoration: 'none' }}>
              <Button variant="secondary">Back to questionnaires</Button>
            </Link>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)', maxWidth: '48rem' }}>
      <PageHeader
        title="New questionnaire"
        subtitle="Paste a questionnaire or upload a CAIQ/SIG XLSX."
      />

      <Alert variant="info">
        Responses cite framework criteria by default. For evidence-backed responses that cite your
        actual policies and SOPs,{' '}
        <Link to={`/w/${slug}/app/evidence`} style={{ color: 'inherit', fontWeight: 700 }}>
          upload documentation first →
        </Link>
      </Alert>

      {uploadedPendingMapping ? (
        <Alert variant="warning">
          <strong>{uploadedPendingMapping.title}</strong> was uploaded but its column mapping isn't
          confirmed yet.{' '}
          <Link
            to={`/w/${slug}/app/questionnaires/new?resume=${encodeURIComponent(uploadedPendingMapping.id)}`}
            style={{ color: 'inherit', fontWeight: 700 }}
          >
            Resume mapping →
          </Link>{' '}
          — uploading again would create a second, separate row for the same file.
        </Alert>
      ) : null}

      <div
        role="tablist"
        aria-label="Ingest mode"
        style={{
          display: 'inline-flex',
          gap: 'var(--space-1)',
          padding: 'var(--space-1)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-input)',
          background: 'var(--surface)',
          width: 'fit-content',
        }}
      >
        {(['paste', 'upload'] as Mode[]).map((m) => {
          const active = mode === m;
          return (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setMode(m)}
              style={{
                border: 'none',
                background: active ? 'var(--surface-elevated, var(--surface))' : 'transparent',
                color: active ? 'var(--text)' : 'var(--text-muted)',
                padding: 'var(--space-2) var(--space-4)',
                borderRadius: 'var(--radius-input)',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-2xs)',
                cursor: 'pointer',
              }}
            >
              {m === 'paste' ? 'Paste' : 'Upload XLSX'}
            </button>
          );
        })}
      </div>

      {error ? <Alert variant="error">{error}</Alert> : null}

      <Field id="qn-title" label="Title" hint="What your team will see in the list.">
        <Input
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          placeholder="Acme security questionnaire — Q3 2026"
          disabled={busy}
        />
      </Field>

      <Field id="qn-due-date" label="Buyer deadline" hint="Optional — when the buyer needs this back. Powers the Deadline column on the list.">
        <Input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.currentTarget.value)}
          disabled={busy}
        />
      </Field>

      {mode === 'paste' ? (
        <Field
          id="qn-paste"
          label="Questionnaire text"
          hint="Paste the whole questionnaire. Every question on its own line is fine; sections and numbering are preserved."
        >
          <Textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.currentTarget.value)}
            placeholder={'1. Do you have a documented information-security policy?\n2. When was your last third-party penetration test?\n...'}
            rows={16}
            disabled={busy}
          />
        </Field>
      ) : (
        <Field
          id="qn-file"
          label="XLSX file"
          hint="CAIQ v4 and SIG-Lite compatible. XLSX only in v1; PDF/DOCX ships in v1.1."
        >
          <input
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
            disabled={busy}
            style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-2xs)' }}
          />
        </Field>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <Button
          variant="primary"
          onClick={submit}
          disabled={busy || Boolean(uploadedPendingMapping)}
          title={uploadedPendingMapping ? 'Resume the pending upload above before starting a new one.' : undefined}
        >
          {busy ? 'Working…' : mode === 'paste' ? 'Create + extract' : 'Upload + choose columns'}
        </Button>
        <Link to={listHref} style={{ textDecoration: 'none' }}>
          <Button variant="secondary" disabled={busy}>
            Cancel
          </Button>
        </Link>
      </div>

      {modal ? (
        <MappingModal
          state={modal}
          busy={busy}
          onChange={(next) => setModal({ ...modal, mapping: next })}
          onConfirm={confirmMapping}
          onCancel={cancelMapping}
        />
      ) : null}
    </div>
  );
}

// ---- Mapping modal ---------------------------------------------------

interface MappingModalProps {
  state: MappingModalState;
  busy: boolean;
  onChange: (mapping: XlsxMappingProposal) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

function MappingModal({ state, busy, onChange, onConfirm, onCancel }: MappingModalProps) {
  const { proposal, sample, mapping, title, conflict, error } = state;

  const set = (field: keyof XlsxMappingProposal, value: string) => {
    onChange({ ...mapping, [field]: value === '' ? null : value });
  };

  const renderOptions = () => (
    <>
      <option value="">— None —</option>
      {sample.headers.map((h) => (
        <option key={h} value={h}>{h}</option>
      ))}
    </>
  );

  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    const previous = document.activeElement as HTMLElement | null;
    dialog?.showModal();
    return () => { dialog?.close(); previous?.focus(); };
  }, []);

  return (
    <dialog ref={dialogRef} onCancel={e => { e.preventDefault(); if (!busy) onCancel(); }}
      aria-labelledby="mapping-modal-title"
      style={{
        position: 'fixed',
        margin: 0, width: '100%', height: '100%', maxWidth: 'none', maxHeight: 'none', border: 0,
        boxSizing: 'border-box',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-6)',
        zIndex: 100,
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          padding: 'var(--space-6)',
          maxWidth: '52rem',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          display: 'grid',
          gap: 'var(--space-4)',
          fontFamily: 'var(--font-ui)',
        }}
      >
        <header>
          <h2
            id="mapping-modal-title"
            style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              fontSize: 'var(--font-size-xl)',
              color: 'var(--text)',
            }}
          >
            Confirm column mapping
          </h2>
          <p
            style={{
              margin: 'var(--space-2) 0 0',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-2xs)',
            }}
          >
            <strong style={{ color: 'var(--text)' }}>{title}</strong> — pick which spreadsheet column
            carries each field. We pre-filled our best guess for each one{proposal.confidence < 0.6 ? ' (low confidence — check these)' : ''};
            override anything that looks wrong.
          </p>
        </header>

        {conflict ? (
          <Alert variant="info">{conflict}</Alert>
        ) : (
          <>
            {error ? <Alert variant="error">{error}</Alert> : null}
            <div style={{ display: 'grid', gap: 'var(--space-3)', gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <Field id="map-question" label="Question text (required)" hint="The cell that holds the auditor's question.">
              <Select
                value={mapping.question_col ?? ''}
                onChange={(e) => set('question_col', e.currentTarget.value)}
                disabled={busy}
              >
                {renderOptions()}
              </Select>
            </Field>
            <Field id="map-response" label="Response (optional)" hint="Customer-supplied answer, if any.">
              <Select
                value={mapping.response_col ?? ''}
                onChange={(e) => set('response_col', e.currentTarget.value)}
                disabled={busy}
              >
                {renderOptions()}
              </Select>
            </Field>
            <Field id="map-section" label="Section (optional)" hint="Category or heading (e.g. IAM, DSI).">
              <Select
                value={mapping.section_col ?? ''}
                onChange={(e) => set('section_col', e.currentTarget.value)}
                disabled={busy}
              >
                {renderOptions()}
              </Select>
            </Field>
            <Field id="map-evidence" label="Evidence link (optional)" hint="URL or reference to supporting evidence.">
              <Select
                value={mapping.evidence_col ?? ''}
                onChange={(e) => set('evidence_col', e.currentTarget.value)}
                disabled={busy}
              >
                {renderOptions()}
              </Select>
            </Field>
            </div>
          </>
        )}

        {!conflict && sample.sampleRows.length > 0 ? (
          <details>
            <summary style={{ cursor: 'pointer', fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
              Preview the first {sample.sampleRows.length} data row{sample.sampleRows.length === 1 ? '' : 's'}
            </summary>
            <div
              style={{
                marginTop: 'var(--space-3)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-input)',
                overflowX: 'auto',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-3xs)' }}>
                <thead>
                  <tr style={{ background: 'var(--surface-elevated, var(--surface))' }}>
                    {sample.headers.map((h) => (
                      <th key={h} style={{ padding: 'var(--space-2)', textAlign: 'left', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sample.sampleRows.map((row, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                      {sample.headers.map((h) => (
                        <td key={h} style={{ padding: 'var(--space-2)', color: 'var(--text-muted)', maxWidth: '20rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row[h] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        ) : null}

        <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {conflict ? 'Back to questionnaires' : 'Cancel'}
          </Button>
          {conflict ? null : (
            <Button variant="primary" onClick={onConfirm} disabled={busy || !mapping.question_col}>
              {busy ? 'Extracting…' : 'Confirm mapping + extract'}
            </Button>
          )}
        </div>
      </div>
    </dialog>
  );
}
